import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BagDetailSale } from '../types';

type Props = {
  sales: BagDetailSale[];
};

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getSaleNumber = (sale: BagDetailSale): string => {
  if (sale.numero) return `#${sale.numero}`;
  return `#${sale.id.slice(-6)}`;
};

const getItemsSummary = (sale: BagDetailSale): string =>
  sale.sale_items
    .map((item) => item.products?.name ?? '')
    .filter(Boolean)
    .join(', ');

const getSaleDate = (sale: BagDetailSale): string => {
  if (!sale.created_at) return '';
  return format(new Date(sale.created_at), 'dd/MM', { locale: ptBR });
};

export const BagSalesList = ({ sales }: Props) => {
  if (sales.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma venda nesta sacolinha.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {sales.map((sale) => {
        const cancelled = sale.payment_status === 'cancelled';
        const summary = getItemsSummary(sale);
        const date = getSaleDate(sale);

        return (
          <li
            key={sale.id}
            className={cn(
              'flex items-center justify-between gap-2 rounded-md border p-3 text-sm',
              cancelled && 'opacity-60'
            )}
          >
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    'font-medium',
                    cancelled && 'line-through text-muted-foreground'
                  )}
                >
                  {getSaleNumber(sale)}
                </span>
                {date && (
                  <span className="text-muted-foreground">{date}</span>
                )}
                <span
                  className={cn(
                    'font-medium',
                    cancelled && 'line-through text-muted-foreground'
                  )}
                >
                  {formatCurrency(sale.total ?? 0)}
                </span>
              </div>
              {summary && (
                <span
                  className={cn(
                    'text-muted-foreground truncate',
                    cancelled && 'line-through'
                  )}
                >
                  {summary}
                </span>
              )}
            </div>
            <Button asChild variant="ghost" size="sm">
              <a href={`/vendas?sale=${sale.id}`}>ver</a>
            </Button>
          </li>
        );
      })}
    </ul>
  );
};
